import React, { useEffect, useMemo, useRef, useState } from 'react';
import { HubConnectionBuilder, LogLevel } from '@microsoft/signalr';
import aulaService from '../../services/AulaService';
import apiService from '../../services/ApiService';
import resolveHubBaseUrl from '../../utils/hubBaseUrl';
import './AulaStatsModal.css';

const FLASH_DURATION_MS = 2200;
const ALERT_DURATION_MS = 3200;
const REFRESH_DEBOUNCE_MS = 450;

const getTodayDate = () => {
  const now = new Date();
  const month = `${now.getMonth() + 1}`.padStart(2, '0');
  const day = `${now.getDate()}`.padStart(2, '0');
  return `${now.getFullYear()}-${month}-${day}`;
};

const shiftDate = (baseDate, diffDays) => {
  const date = new Date(`${baseDate}T00:00:00`);
  date.setDate(date.getDate() + diffDays);
  const month = `${date.getMonth() + 1}`.padStart(2, '0');
  const day = `${date.getDate()}`.padStart(2, '0');
  return `${date.getFullYear()}-${month}-${day}`;
};

const formatDateTime = (value) => {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleString('es-AR', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit'
  });
};

const renderPresenceRole = (role) => {
  switch ((role || '').toLowerCase()) {
    case 'alumno':
      return 'Alumno';
    case 'profesor':
      return 'Profesor';
    case 'profesor particular':
      return 'Profesor particular';
    case 'dueño de cuenta':
    case 'dueno particular':
      return 'Dueño de cuenta';
    case 'alumno particular':
      return 'Alumno particular';
    case 'admin colegio':
      return 'Admin colegio';
    case 'administrador':
      return 'Administrador';
    default:
      return role || 'Usuario';
  }
};

const normalizeGuid = (value) => (value || '').toString().trim().toLowerCase();

const getHubBaseUrl = () =>
  resolveHubBaseUrl();

const buildPresenceSignature = (presencia) => {
  const usuarios = Array.isArray(presencia?.usuarios) ? presencia.usuarios : [];
  return JSON.stringify({
    activos: presencia?.activos ?? 0,
    alumnosActivos: presencia?.alumnosActivos ?? 0,
    usuarios: usuarios
      .map((user) => `${user.displayName || ''}|${user.roleNormalized || ''}|${user.connectedAtUtc || ''}`)
      .sort()
  });
};

const buildCallSignature = (videollamada) => {
  const eventos = Array.isArray(videollamada?.eventos) ? videollamada.eventos : [];
  return JSON.stringify({
    activa: Boolean(videollamada?.activa),
    iniciadaPor: videollamada?.iniciadaPor || '',
    iniciadaEnUtc: videollamada?.iniciadaEnUtc || '',
    eventos: eventos
      .map((item) => `${item.nombre || ''}|${item.tipo || ''}|${item.rol || ''}|${item.fechaUtc || ''}`)
      .sort()
  });
};

const buildAssistantsCatalogSignature = (items) =>
  JSON.stringify(
    (Array.isArray(items) ? items : [])
      .map((assistant) => `${assistant.assistantId || ''}|${assistant.nombre || ''}|${assistant.tipo || ''}`)
      .sort()
  );

const buildAssistantsTopSignature = (items) =>
  JSON.stringify(
    (Array.isArray(items) ? items : [])
      .map((assistant) => `${assistant.assistantId || ''}|${assistant.nombre || ''}|${assistant.totalConsultas || 0}|${assistant.ultimoUsoUtc || ''}`)
      .sort()
  );

const buildRecentActivitySignature = (items) =>
  JSON.stringify(
    (Array.isArray(items) ? items : [])
      .map((item) => `${item.evento || ''}|${item.actor || ''}|${item.detalle || ''}|${item.fechaUtc || ''}`)
      .sort()
  );

const defaultStatsService = (id, options) => aulaService.getAulaStats(id, options);

const extractComparableValues = (stats) => {
  const currentSummary = stats?.resumen?.estadoActual || {};
  const rangeSummary = stats?.resumen?.actividadEnRango || {};
  const presencia = stats?.presencia || {};

  return {
    currentTotalArchivos: currentSummary.totalArchivos ?? 0,
    currentTotalAsistentes: currentSummary.totalAsistentes ?? 0,
    currentTotalMensajesForo: currentSummary.totalMensajesForo ?? 0,
    currentChatConectadosAhora: currentSummary.chatConectadosAhora ?? 0,
    currentPizarraConectadosAhora: currentSummary.pizarraConectadosAhora ?? 0,
    currentVideollamadaActivaAhora: Boolean(currentSummary.videollamadaActivaAhora),
    currentUltimoArchivoSubido: currentSummary.ultimoArchivoSubido || '',
    currentUltimoMovimientoForo: currentSummary.ultimoMovimientoForo || '',
    currentVideollamadaIniciadaPor: currentSummary.videollamadaIniciadaPor || '',
    rangeVisitasAula: rangeSummary.visitasAula ?? 0,
    rangeChatsGrupales: rangeSummary.chatsGrupales ?? 0,
    rangeConsultasAsistentes: rangeSummary.consultasAsistentes ?? 0,
    rangeArchivosDescargados: rangeSummary.archivosDescargados ?? 0,
    rangeMensajesForo: rangeSummary.mensajesForo ?? 0,
    rangeRespuestasHoy: rangeSummary.respuestasHoy ?? 0,
    rangePizarrasIniciadas: rangeSummary.pizarrasIniciadas ?? 0,
    rangeParticipacionesEnPizarra: rangeSummary.participacionesEnPizarra ?? 0,
    rangeVideollamadasIniciadas: rangeSummary.videollamadasIniciadas ?? 0,
    rangeParticipacionesEnVideollamada: rangeSummary.participacionesEnVideollamada ?? 0,
    rangeAlumnosConActividad: rangeSummary.alumnosConActividad ?? 0,
    panelChat: buildPresenceSignature(presencia?.chat),
    panelBoard: buildPresenceSignature(presencia?.pizarra),
    panelCall: buildCallSignature(presencia?.videollamada),
    assistantsCatalog: buildAssistantsCatalogSignature(stats?.asistentes?.catalogo),
    assistantsTop: buildAssistantsTopSignature(stats?.asistentes?.top),
    recentActivity: buildRecentActivitySignature(stats?.actividadReciente)
  };
};

const getChangedFields = (previousStats, nextStats) => {
  if (!previousStats || !nextStats) return [];

  const previousValues = extractComparableValues(previousStats);
  const nextValues = extractComparableValues(nextStats);

  return Object.keys(nextValues).filter((field) => previousValues[field] !== nextValues[field]);
};

const buildAlertText = (payload) => {
  const label = payload?.etiqueta || payload?.tipoActualizacion || 'las estadísticas del aula';
  return `Se actualizó ${label.toString().toLowerCase()}.`;
};

const AulaStatsModal = ({
  open,
  aula,
  onClose,
  statsService = defaultStatsService,
  hubPath = '/hubs/aula-stats',
  title = 'Estadísticas del aula'
}) => {
  const [fromDate, setFromDate] = useState(getTodayDate());
  const [toDate, setToDate] = useState(getTodayDate());
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [error, setError] = useState('');
  const [highlightedFields, setHighlightedFields] = useState({});
  const [liveAlert, setLiveAlert] = useState(null);

  const connectionRef = useRef(null);
  const statsRef = useRef(null);
  const fromDateRef = useRef(fromDate);
  const toDateRef = useRef(toDate);
  const refreshTimerRef = useRef(null);
  const alertTimerRef = useRef(null);
  const flashTimersRef = useRef({});

  const aulaId = aula?.id || aula?.Id || null;
  const aulaNombre = aula?.nombre || aula?.Nombre || 'Aula';

  useEffect(() => {
    statsRef.current = stats;
  }, [stats]);

  useEffect(() => {
    fromDateRef.current = fromDate;
  }, [fromDate]);

  useEffect(() => {
    toDateRef.current = toDate;
  }, [toDate]);

  const clearFlashTimers = () => {
    Object.values(flashTimersRef.current).forEach((timerId) => clearTimeout(timerId));
    flashTimersRef.current = {};
  };

  const clearRefreshTimer = () => {
    if (refreshTimerRef.current) {
      clearTimeout(refreshTimerRef.current);
      refreshTimerRef.current = null;
    }
  };

  const clearAlertTimer = () => {
    if (alertTimerRef.current) {
      clearTimeout(alertTimerRef.current);
      alertTimerRef.current = null;
    }
  };

  const markChangedFields = (fields, payload, forceAlert = false) => {
    if (fields.length > 0) {
      setHighlightedFields((prev) => {
        const next = { ...prev };
        fields.forEach((field) => {
          next[field] = true;
        });
        return next;
      });

      fields.forEach((field) => {
        if (flashTimersRef.current[field]) {
          clearTimeout(flashTimersRef.current[field]);
        }

        flashTimersRef.current[field] = setTimeout(() => {
          setHighlightedFields((prev) => ({ ...prev, [field]: false }));
          delete flashTimersRef.current[field];
        }, FLASH_DURATION_MS);
      });
    }

    if (!fields.length && !forceAlert) {
      return;
    }

    setLiveAlert({
      id: `${payload?.eventId || payload?.fechaUtc || Date.now()}`,
      text: buildAlertText(payload)
    });

    clearAlertTimer();
    alertTimerRef.current = setTimeout(() => {
      setLiveAlert(null);
    }, ALERT_DURATION_MS);
  };

  const loadStats = async (
    nextFrom = fromDateRef.current,
    nextTo = toDateRef.current,
    options = {}
  ) => {
    if (!aulaId) return null;

    const {
      showLoader = true,
      silentError = false,
      compareWithCurrent = false,
      payload = null,
      forceAlert = false
    } = options;

    if (showLoader) {
      setLoading(true);
    } else {
      setIsRefreshing(true);
    }

    if (!silentError) {
      setError('');
    }

    const previousStats = compareWithCurrent ? statsRef.current : null;

    try {
      const response = await statsService(aulaId, {
        from: nextFrom,
        to: nextTo
      });

      const nextStats = response || null;
      statsRef.current = nextStats;
      setStats(nextStats);

      if (compareWithCurrent && previousStats && nextStats) {
        markChangedFields(getChangedFields(previousStats, nextStats), payload, forceAlert);
      } else if (forceAlert) {
        markChangedFields([], payload, true);
      }

      return nextStats;
    } catch (err) {
      if (!silentError) {
        setStats(null);
        setError(err.message || 'No pudimos cargar las estadísticas del aula.');
      } else {
        console.warn('No pudimos refrescar las estadísticas del aula en vivo', err);
      }

      return null;
    } finally {
      if (showLoader) {
        setLoading(false);
      } else {
        setIsRefreshing(false);
      }
    }
  };

  useEffect(() => {
    if (!open) return undefined;

    const today = getTodayDate();
    setFromDate(today);
    setToDate(today);
    setHighlightedFields({});
    setLiveAlert(null);

    return undefined;
  }, [open, aulaId]);

  useEffect(() => {
    if (!open || !aulaId) return undefined;

    const today = getTodayDate();
    void loadStats(today, today);

    return undefined;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, aulaId]);

  useEffect(() => {
    if (!open || !aulaId) return undefined;

    let disposed = false;
    const hubBase = getHubBaseUrl();
    const normalizedHubPath = hubPath.startsWith('/') ? hubPath : `/${hubPath}`;
    const connection = new HubConnectionBuilder()
      .withUrl(`${hubBase}${normalizedHubPath}`, {
        accessTokenFactory: () => apiService.getTokenFromCookies() || '',
        withCredentials: true
      })
      .withAutomaticReconnect()
      .configureLogging(LogLevel.Warning)
      .build();

    const joinGroup = async () => {
      await connection.invoke('JoinAulaStats', aulaId.toString());
    };

    const queueRefresh = (payload) => {
      if (normalizeGuid(payload?.aulaId) && normalizeGuid(payload?.aulaId) !== normalizeGuid(aulaId)) {
        return;
      }

      clearRefreshTimer();
      refreshTimerRef.current = setTimeout(async () => {
        const nextStats = await loadStats(fromDateRef.current, toDateRef.current, {
          showLoader: false,
          silentError: true,
          compareWithCurrent: true,
          payload
        });

        if (disposed || !nextStats) return;
      }, REFRESH_DEBOUNCE_MS);
    };

    connection.on('AulaStatsUpdated', queueRefresh);
    connection.onreconnected(async () => {
      try {
        await joinGroup();
      } catch (err) {
        console.warn('No se pudo reenganchar el hub de estadísticas del aula', err);
      }
    });

    (async () => {
      try {
        await connection.start();
        if (disposed) {
          await connection.stop();
          return;
        }

        await joinGroup();
        connectionRef.current = connection;
      } catch (err) {
        console.warn('No se pudo conectar el hub de estadísticas del aula', err);
      }
    })();

    return () => {
      disposed = true;
      clearRefreshTimer();
      clearAlertTimer();
      clearFlashTimers();
      const activeConnection = connectionRef.current || connection;
      activeConnection.stop().catch(() => {});
      connectionRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, aulaId, hubPath, statsService]);

  const currentSummary = useMemo(() => stats?.resumen?.estadoActual || {}, [stats]);
  const rangeSummary = useMemo(() => stats?.resumen?.actividadEnRango || {}, [stats]);
  const presencia = stats?.presencia || {};
  const assistantsTop = Array.isArray(stats?.asistentes?.top) ? stats.asistentes.top : [];
  const assistantsCatalog = Array.isArray(stats?.asistentes?.catalogo) ? stats.asistentes.catalogo : [];
  const recentActivity = Array.isArray(stats?.actividadReciente) ? stats.actividadReciente : [];

  const isHighlighted = (...keys) => keys.some((key) => Boolean(highlightedFields[key]));

  const currentCards = useMemo(() => ([
    { fieldKey: 'currentTotalArchivos', label: 'Archivos cargados', value: currentSummary.totalArchivos ?? 0, icon: 'fa-folder-open' },
    { fieldKey: 'currentTotalAsistentes', label: 'Asistentes disponibles', value: currentSummary.totalAsistentes ?? 0, icon: 'fa-robot' },
    { fieldKey: 'currentTotalMensajesForo', label: 'Mensajes en foro', value: currentSummary.totalMensajesForo ?? 0, icon: 'fa-comments' },
    { fieldKey: 'currentChatConectadosAhora', label: 'Chat en vivo', value: currentSummary.chatConectadosAhora ?? 0, icon: 'fa-user-friends' },
    { fieldKey: 'currentPizarraConectadosAhora', label: 'Pizarra en vivo', value: currentSummary.pizarraConectadosAhora ?? 0, icon: 'fa-chalkboard' },
    { fieldKey: 'currentVideollamadaActivaAhora', label: 'Videollamada activa', value: currentSummary.videollamadaActivaAhora ? 'Sí' : 'No', icon: 'fa-video' }
  ]), [currentSummary]);

  const rangeCards = useMemo(() => ([
    { fieldKey: 'rangeVisitasAula', label: 'Visitas al aula', value: rangeSummary.visitasAula ?? 0, icon: 'fa-eye' },
    { fieldKey: 'rangeChatsGrupales', label: 'Chats grupales', value: rangeSummary.chatsGrupales ?? 0, icon: 'fa-comments' },
    { fieldKey: 'rangeConsultasAsistentes', label: 'Consultas a asistentes', value: rangeSummary.consultasAsistentes ?? 0, icon: 'fa-robot' },
    { fieldKey: 'rangeArchivosDescargados', label: 'Descargas de archivos', value: rangeSummary.archivosDescargados ?? 0, icon: 'fa-download' },
    { fieldKey: 'rangeMensajesForo', label: 'Mensajes en foro', value: rangeSummary.mensajesForo ?? 0, icon: 'fa-comment-dots' },
    { fieldKey: 'rangeRespuestasHoy', label: 'Respuestas hoy', value: rangeSummary.respuestasHoy ?? 0, icon: 'fa-reply' }
  ]), [rangeSummary]);

  if (!open) return null;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div
        className="modal-card aula-stats-modal"
        style={{ maxWidth: '1180px', width: 'min(1180px, 96vw)' }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="modal-header aula-stats-modal-header">
          <div>
            <h3><i className="fas fa-chart-line" /> {title}</h3>
            <p>{aulaNombre}</p>
          </div>
          <button className="close-btn" onClick={onClose}>
            <i className="fas fa-times" />
          </button>
        </div>

        <div className="modal-body aula-stats-modal-body">
          <div className="aula-stats-toolbar">
            <div className="aula-stats-filters">
              <label>
                <span>Desde</span>
                <input type="date" value={fromDate} onChange={(e) => setFromDate(e.target.value)} />
              </label>
              <label>
                <span>Hasta</span>
                <input type="date" value={toDate} onChange={(e) => setToDate(e.target.value)} />
              </label>
            </div>
            <div className="aula-stats-toolbar-actions">
              <button
                className="btn btn-secondary"
                type="button"
                onClick={() => {
                  const today = getTodayDate();
                  setFromDate(today);
                  setToDate(today);
                  void loadStats(today, today);
                }}
              >
                Hoy
              </button>
              <button
                className="btn btn-secondary"
                type="button"
                onClick={() => {
                  const today = getTodayDate();
                  const from = shiftDate(today, -6);
                  setFromDate(from);
                  setToDate(today);
                  void loadStats(from, today);
                }}
              >
                Últimos 7 días
              </button>
              <button
                className="btn btn-secondary"
                type="button"
                onClick={() => {
                  const today = getTodayDate();
                  const from = shiftDate(today, -29);
                  setFromDate(from);
                  setToDate(today);
                  void loadStats(from, today);
                }}
              >
                Últimos 30 días
              </button>
              <button
                className="btn btn-secondary"
                type="button"
                onClick={() => loadStats(fromDate, toDate, {
                  showLoader: false,
                  compareWithCurrent: true,
                  payload: { etiqueta: 'Actualización manual' },
                  forceAlert: true
                })}
                disabled={loading || isRefreshing}
              >
                <i className={`fas ${isRefreshing ? 'fa-spinner fa-spin' : 'fa-sync-alt'}`} />
                Actualizar
              </button>
              <button className="btn btn-primary" type="button" onClick={() => loadStats()} disabled={loading}>
                <i className={`fas ${loading ? 'fa-spinner fa-spin' : 'fa-filter'}`} />
                Aplicar
              </button>
            </div>
          </div>

          {liveAlert && (
            <div className="aula-stats-live-alert">
              <i className="fas fa-bolt" />
              <span>{liveAlert.text}</span>
              {isRefreshing && <small>Sincronizando…</small>}
            </div>
          )}

          {loading && (
            <div className="data-table-loading">
              <div className="loading-spinner">
                <i className="fas fa-spinner fa-spin" />
              </div>
              <p>Cargando estadísticas del aula...</p>
            </div>
          )}

          {!loading && error && (
            <div className="data-table-empty">
              <div className="empty-icon">
                <i className="fas fa-exclamation-triangle" />
              </div>
              <p>{error}</p>
            </div>
          )}

          {!loading && !error && stats && (
            <>
              <section className="aula-stats-section">
                <div className="aula-stats-section-header">
                  <div>
                    <h4>Estado actual del aula</h4>
                    <p>Snapshot en tiempo real y acumulado actual.</p>
                  </div>
                  <span className={`status-chip ${currentSummary.videollamadaActivaAhora ? 'success' : 'info'}${isHighlighted('currentVideollamadaActivaAhora', 'currentVideollamadaIniciadaPor') ? ' is-updated' : ''}`}>
                    {currentSummary.videollamadaActivaAhora ? 'Videollamada activa' : 'Sin videollamada en curso'}
                  </span>
                </div>
                <div className="aula-stats-grid">
                  {currentCards.map((card) => (
                    <article key={card.label} className={`aula-stats-card${isHighlighted(card.fieldKey) ? ' is-updated' : ''}`}>
                      <div className="aula-stats-card-icon">
                        <i className={`fas ${card.icon}`} />
                      </div>
                      <div>
                        <p>{card.label}</p>
                        <strong>{card.value}</strong>
                      </div>
                    </article>
                  ))}
                </div>
                <div className="aula-stats-meta-grid">
                  <div className={`aula-stats-meta-card${isHighlighted('currentUltimoArchivoSubido') ? ' is-updated' : ''}`}>
                    <span>Último archivo subido</span>
                    <strong>{formatDateTime(currentSummary.ultimoArchivoSubido)}</strong>
                  </div>
                  <div className={`aula-stats-meta-card${isHighlighted('currentUltimoMovimientoForo') ? ' is-updated' : ''}`}>
                    <span>Último movimiento en foro</span>
                    <strong>{formatDateTime(currentSummary.ultimoMovimientoForo)}</strong>
                  </div>
                  <div className={`aula-stats-meta-card${isHighlighted('currentVideollamadaIniciadaPor') ? ' is-updated' : ''}`}>
                    <span>Videollamada iniciada por</span>
                    <strong>{currentSummary.videollamadaIniciadaPor || '—'}</strong>
                  </div>
                </div>
              </section>

              <section className="aula-stats-section">
                <div className="aula-stats-section-header">
                  <div>
                    <h4>Actividad del rango</h4>
                    <p>Desde {stats?.rango?.from || fromDate} hasta {stats?.rango?.to || toDate}.</p>
                  </div>
                </div>
                <div className="aula-stats-grid">
                  {rangeCards.map((card) => (
                    <article key={card.label} className={`aula-stats-card accent${isHighlighted(card.fieldKey) ? ' is-updated' : ''}`}>
                      <div className="aula-stats-card-icon">
                        <i className={`fas ${card.icon}`} />
                      </div>
                      <div>
                        <p>{card.label}</p>
                        <strong>{card.value}</strong>
                      </div>
                    </article>
                  ))}
                </div>
                <div className="aula-stats-range-summary">
                  <span className={`status-chip info${isHighlighted('rangePizarrasIniciadas') ? ' is-updated' : ''}`}>Pizarras iniciadas: {rangeSummary.pizarrasIniciadas ?? 0}</span>
                  <span className={`status-chip info${isHighlighted('rangeParticipacionesEnPizarra') ? ' is-updated' : ''}`}>Ingresos a pizarra: {rangeSummary.participacionesEnPizarra ?? 0}</span>
                  <span className={`status-chip info${isHighlighted('rangeVideollamadasIniciadas') ? ' is-updated' : ''}`}>Videollamadas iniciadas: {rangeSummary.videollamadasIniciadas ?? 0}</span>
                  <span className={`status-chip info${isHighlighted('rangeParticipacionesEnVideollamada') ? ' is-updated' : ''}`}>Ingresos a videollamada: {rangeSummary.participacionesEnVideollamada ?? 0}</span>
                  <span className={`status-chip info${isHighlighted('rangeAlumnosConActividad') ? ' is-updated' : ''}`}>Alumnos con actividad: {rangeSummary.alumnosConActividad ?? 0}</span>
                </div>
              </section>

              <section className="aula-stats-section">
                <div className="aula-stats-section-header">
                  <div>
                    <h4>Presencia y participación</h4>
                    <p>Conectados ahora en chat y pizarra, y actividad reciente de videollamada.</p>
                  </div>
                </div>
                <div className="aula-stats-live-grid">
                  <div className={`aula-stats-panel${isHighlighted('panelChat') ? ' is-updated' : ''}`}>
                    <div className="aula-stats-panel-title">
                      <i className="fas fa-comments" />
                      <span>Chat del aula</span>
                    </div>
                    <p className="aula-stats-panel-caption">
                      Activos: {presencia?.chat?.activos ?? 0} | Alumnos: {presencia?.chat?.alumnosActivos ?? 0}
                    </p>
                    <div className="aula-stats-chip-list">
                      {(presencia?.chat?.usuarios || []).length > 0 ? (
                        (presencia.chat.usuarios || []).map((user) => (
                          <span key={`chat-${user.displayName}-${user.roleNormalized}`} className="curso-filter-chip">
                            <i className="fas fa-user" />
                            {user.displayName} · {renderPresenceRole(user.role)}
                          </span>
                        ))
                      ) : (
                        <span className="status-chip pending">Sin usuarios conectados</span>
                      )}
                    </div>
                  </div>

                  <div className={`aula-stats-panel${isHighlighted('panelBoard') ? ' is-updated' : ''}`}>
                    <div className="aula-stats-panel-title">
                      <i className="fas fa-chalkboard" />
                      <span>Pizarra colaborativa</span>
                    </div>
                    <p className="aula-stats-panel-caption">
                      Activos: {presencia?.pizarra?.activos ?? 0} | Alumnos: {presencia?.pizarra?.alumnosActivos ?? 0}
                    </p>
                    <div className="aula-stats-chip-list">
                      {(presencia?.pizarra?.usuarios || []).length > 0 ? (
                        (presencia.pizarra.usuarios || []).map((user) => (
                          <span key={`board-${user.displayName}-${user.roleNormalized}`} className="curso-filter-chip">
                            <i className="fas fa-user-edit" />
                            {user.displayName} · {renderPresenceRole(user.role)}
                          </span>
                        ))
                      ) : (
                        <span className="status-chip pending">Sin usuarios conectados</span>
                      )}
                    </div>
                  </div>

                  <div className={`aula-stats-panel${isHighlighted('panelCall') ? ' is-updated' : ''}`}>
                    <div className="aula-stats-panel-title">
                      <i className="fas fa-video" />
                      <span>Videollamada</span>
                    </div>
                    <p className="aula-stats-panel-caption">
                      {presencia?.videollamada?.activa
                        ? `Sala activa iniciada por ${presencia.videollamada.iniciadaPor || '—'}`
                        : 'Sin sala activa en este momento'}
                    </p>
                    <div className="aula-stats-activity-list compact">
                      {(presencia?.videollamada?.eventos || []).length > 0 ? (
                        (presencia.videollamada.eventos || []).map((item, index) => (
                          <div key={`${item.nombre}-${item.fechaUtc}-${index}`} className="aula-stats-activity-item">
                            <div>
                              <strong>{item.nombre}</strong>
                              <span>{item.tipo} · {renderPresenceRole(item.rol)}</span>
                            </div>
                            <time>{formatDateTime(item.fechaUtc)}</time>
                          </div>
                        ))
                      ) : (
                        <span className="status-chip pending">Sin movimientos de videollamada en el rango</span>
                      )}
                    </div>
                  </div>
                </div>
              </section>

              <section className="aula-stats-section">
                <div className="aula-stats-section-header">
                  <div>
                    <h4>Asistentes del aula</h4>
                    <p>Catálogo disponible y ranking de consultas dentro del rango filtrado.</p>
                  </div>
                </div>
                <div className="aula-stats-live-grid">
                  <div className={`aula-stats-panel${isHighlighted('assistantsCatalog') ? ' is-updated' : ''}`}>
                    <div className="aula-stats-panel-title">
                      <i className="fas fa-layer-group" />
                      <span>Disponibles</span>
                    </div>
                    <div className="aula-stats-chip-list">
                      {assistantsCatalog.length > 0 ? (
                        assistantsCatalog.map((assistant) => {
                          const isClassicAssistant = assistant.tipo === 'GPT clásico' || assistant.tipo === 'Asistente IA';
                          return (
                            <span key={assistant.assistantId || assistant.nombre} className="curso-filter-chip">
                              <i className={`fas ${isClassicAssistant ? 'fa-brain' : 'fa-robot'}`} />
                              {assistant.nombre} · {isClassicAssistant ? 'Asistente IA' : assistant.tipo}
                            </span>
                          );
                        })
                      ) : (
                        <span className="status-chip pending">No hay asistentes asociados</span>
                      )}
                    </div>
                  </div>

                  <div className={`aula-stats-panel${isHighlighted('assistantsTop') ? ' is-updated' : ''}`}>
                    <div className="aula-stats-panel-title">
                      <i className="fas fa-fire" />
                      <span>Top consultas</span>
                    </div>
                    <div className="aula-stats-activity-list">
                      {assistantsTop.length > 0 ? (
                        assistantsTop.map((assistant) => (
                          <div key={`${assistant.assistantId}-${assistant.nombre}`} className="aula-stats-activity-item">
                            <div>
                              <strong>{assistant.nombre}</strong>
                              <span>{assistant.tipo} · {assistant.totalConsultas} consulta(s)</span>
                            </div>
                            <time>{formatDateTime(assistant.ultimoUsoUtc)}</time>
                          </div>
                        ))
                      ) : (
                        <span className="status-chip pending">Sin consultas a asistentes en el rango</span>
                      )}
                    </div>
                  </div>
                </div>
              </section>

              <section className={`aula-stats-section${isHighlighted('recentActivity') ? ' is-updated' : ''}`}>
                <div className="aula-stats-section-header">
                  <div>
                    <h4>Actividad reciente</h4>
                    <p>Últimos eventos registrados para esta aula dentro del rango.</p>
                  </div>
                </div>
                <div className="aula-stats-activity-list">
                  {recentActivity.length > 0 ? (
                    recentActivity.map((item, index) => (
                      <div key={`${item.evento}-${item.fechaUtc}-${index}`} className="aula-stats-activity-item">
                        <div>
                          <strong>{item.evento}</strong>
                          <span>{item.actor} · {renderPresenceRole(item.rol)}</span>
                          <small>{item.detalle}</small>
                        </div>
                        <time>{formatDateTime(item.fechaUtc)}</time>
                      </div>
                    ))
                  ) : (
                    <div className="data-table-empty compact">
                      <p>No hay actividad registrada para este rango.</p>
                    </div>
                  )}
                </div>
              </section>
            </>
          )}
        </div>

        <div className="modal-footer">
          <button className="btn btn-secondary" onClick={onClose}>
            Cerrar
          </button>
        </div>
      </div>
    </div>
  );
};

export default AulaStatsModal;
